import React, { useMemo, useState } from "react";
import moment from "moment";
import { useQuery } from "react-query";
import { apiPost, getErrorMessage } from "services/api";
import { useStores } from "shared-apis";
import { getItemGroups } from "features/Products/api";
import {
  transformItemsGroup,
  findGroupsAllChildrenIds,
} from "features/Products/helpers";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, MediumText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { Select } from "components/Select";
import { Calendar } from "components/Calendar";
import { TimePicker } from "components/TimePicker";
import { Checkbox } from "components/Checkbox";
import { Accordion } from "components/Accordion";
import { SearchInput } from "components/SearchInput";
import { VerticalSpace, Divider } from "components/Spacing";
import { LoadingView, ErrorView } from "components/Feedback";
import { useModal, LoadingModal, InfoModal } from "components/Modal";

import DaysServices from "utils/days";
import { generateExcelFile } from "utils/excel";
import { ALL_ITEMS_GROUP } from "atoms/catalogAtom";

import styles from "./styles.module.scss";

const isAllItemsGroup = (group) =>
  String(group.itemgroupid) === String(ALL_ITEMS_GROUP.itemgroupid);

const round2 = (val) => (Number.isFinite(val) ? val.toFixed(2) : "0.00");

const calcProfitMetrics = (row) => {
  const total12 = Number(row.total12) || 0;
  const cost123 = Number(row.cost123) || 0;
  return {
    salepercent: cost123
      ? round2(((total12 - cost123) / cost123) * 100)
      : "0.00",
    rentability: total12
      ? round2(((total12 - cost123) / total12) * 100)
      : "0.00",
  };
};

const getBottomRow = (reports, type) => {
  if (!reports.length) return undefined;

  const bottomRow = {};
  const skipFields = new Set(["number", "groupname", "itemname"]);

  for (const row of reports) {
    for (const [key, value] of Object.entries(row)) {
      if (skipFields.has(key)) continue;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) continue;
      bottomRow[key] = (bottomRow[key] ?? 0) + numeric;
    }
  }

  if (type === "profit") {
    Object.assign(bottomRow, calcProfitMetrics(bottomRow));
  }

  return bottomRow;
};

/** Единая страница складского отчёта (движение / прибыль) */
export const ReportPage = ({ title, type, isMultiStore }) => {
  const { openModal, closeModal } = useModal();

  /* ---- фильтры ---- */
  const [dateFrom, setDateFrom] = useState(() =>
    DaysServices.getDayStart(new Date())
  );
  const [dateTo, setDateTo] = useState(() =>
    DaysServices.getDayEnd(new Date())
  );
  const [storeOptionsValue, setStoreOptionsValue] = useState(
    isMultiStore ? [] : null
  );
  const [itemGroupIds, setItemGroupIds] = useState([]);
  const [expanded, setExpanded] = useState(new Set());

  const storesQuery = useStores();
  const storeOptions = useMemo(
    () =>
      (storesQuery.data?.items ?? []).map((s) => ({
        value: String(s.storesid),
        label: s.name,
      })),
    [storesQuery.data]
  );

  const groupsQuery = useQuery(["item-groups"], () => getItemGroups());
  const groups = useMemo(
    () =>
      (groupsQuery.data?.items ?? []).filter((group) => !isAllItemsGroup(group)),
    [groupsQuery.data]
  );
  const tree = useMemo(() => transformItemsGroup(groups), [groups]);

  /* ---- данные отчёта ---- */
  const [reports, setReports] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [search, setSearch] = useState("");

  const filteredReports = useMemo(() => {
    if (!search) return reports;
    const lower = search.toLowerCase();
    return reports.filter((row) =>
      JSON.stringify(row).toLowerCase().includes(lower)
    );
  }, [reports, search]);

  const colDefs = useMemo(
    () =>
      headers.map((header) => ({
        ...header,
        resizable: Boolean(header.resizable),
      })),
    [headers]
  );

  const bottomRow = useMemo(() => getBottomRow(reports, type), [reports, type]);

  /* ---- действия ---- */
  const storeIds = useMemo(() => {
    if (isMultiStore) {
      return (storeOptionsValue ?? []).map((o) => o.value);
    }
    return storeOptionsValue ? [storeOptionsValue.value] : [];
  }, [isMultiStore, storeOptionsValue]);

  const handleGenerate = async () => {
    openModal(<LoadingModal title="Формируем отчёт..." />);
    try {
      const data = await apiPost(`reports/${type}`, {
        dateFrom,
        dateTo,
        storeIds,
        itemGroupIds,
      });

      if (data?.message) {
        openModal(<InfoModal title={data.message} onClose={closeModal} />);
        return;
      }

      if (data?.headers?.length) {
        let tableData = (data.tableData ?? []).map((row, index) => ({
          ...row,
          number: index + 1,
        }));
        if (type === "profit") {
          tableData = tableData.map((row) => ({
            ...row,
            ...calcProfitMetrics(row),
          }));
        }
        setReports(tableData);
        setHeaders(data.headers);
      } else {
        setReports([]);
        setHeaders([]);
      }
      closeModal();
    } catch (e) {
      openModal(<InfoModal title={getErrorMessage(e)} onClose={closeModal} />);
    }
  };

  const handleExcel = () => {
    if (!reports.length) return;
    const dateFormat = (date) => moment(date).format("YYYY_MM_DD");
    generateExcelFile({
      data: reports,
      headers,
      fileName: `fp-report-${type}-${dateFormat(dateFrom)}-${dateFormat(dateTo)}`,
    });
  };

  /* ---- дерево групп ---- */
  const toggleAccordion = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGroupSelect = (groupId) => {
    setItemGroupIds((prev) => {
      const childrenIds = findGroupsAllChildrenIds(groups, [groupId]);
      const idsToToggle = new Set(
        [groupId, ...childrenIds].map((id) => String(id))
      );

      if (prev.some((id) => String(id) === String(groupId))) {
        return prev.filter((id) => !idsToToggle.has(String(id)));
      }

      return [...new Set([...prev, groupId, ...childrenIds])];
    });
  };

  const renderGroupTree = (nodes) =>
    nodes.map((node) => {
      const checkbox = (
        <div className={styles.groupRow} key={node.itemgroupid}>
          <Checkbox
            checked={itemGroupIds.includes(node.itemgroupid)}
            onChange={() => handleGroupSelect(node.itemgroupid)}
            label={node.name}
          />
        </div>
      );

      if (node.children?.length) {
        return (
          <Accordion
            key={node.itemgroupid}
            withIcon
            arrowZone
            collapsed={!expanded.has(node.itemgroupid)}
            handleCollapse={() => toggleAccordion(node.itemgroupid)}
            parent={checkbox}
          >
            <div className={styles.treeChildren}>
              {renderGroupTree(node.children)}
            </div>
          </Accordion>
        );
      }
      return checkbox;
    });

  const handleTimeChange = (setter) => (time) => {
    if (!time) return;
    setter((prev) => {
      const next = new Date(prev);
      DaysServices.setDateTime(next, `${time}:00`);
      return next;
    });
  };

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.leftPanel}
        header={<BoldText>Товары</BoldText>}
        footer={
          <ActionsRow>
            <Button
              icon="download"
              variant="secondary"
              onClick={handleGenerate}
            >
              Сформировать
            </Button>
            <Button
              icon="excel"
              onClick={handleExcel}
              disabled={!reports.length}
            >
              Excel
            </Button>
          </ActionsRow>
        }
      >
        <div className={styles.filterRow}>
          <MediumText>Начальная дата:</MediumText>
          <div className={styles.dateTimeRow}>
            <Calendar
              value={dateFrom}
              onChange={(val) =>
                setDateFrom((prev) => {
                  const next = DaysServices.getDayStart(val);
                  next.setHours(
                    prev.getHours(),
                    prev.getMinutes(),
                    prev.getSeconds()
                  );
                  return next;
                })
              }
            />
            <TimePicker
              value={moment(dateFrom).format("HH:mm")}
              onChange={handleTimeChange(setDateFrom)}
            />
          </div>
        </div>
        <VerticalSpace size={12} />
        <div className={styles.filterRow}>
          <MediumText>Конечная дата:</MediumText>
          <div className={styles.dateTimeRow}>
            <Calendar
              value={dateTo}
              onChange={(val) =>
                setDateTo((prev) => {
                  const next = DaysServices.getDayStart(val);
                  next.setHours(
                    prev.getHours(),
                    prev.getMinutes(),
                    prev.getSeconds()
                  );
                  return next;
                })
              }
            />
            <TimePicker
              value={moment(dateTo).format("HH:mm")}
              onChange={handleTimeChange(setDateTo)}
            />
          </div>
        </div>
        <VerticalSpace size={12} />
        <Select
          label="Склад"
          options={storeOptions}
          value={storeOptionsValue}
          onChange={setStoreOptionsValue}
          isMulti={isMultiStore}
          isClearable
          isLoading={storesQuery.isLoading}
          placeholder="Все"
          width={280}
        />
        <VerticalSpace size={16} />
        <Divider />
        <VerticalSpace size={12} />
        <div className={styles.groupButtons}>
          <Button
            onClick={() => setItemGroupIds(groups.map((g) => g.itemgroupid))}
          >
            Выбрать все
          </Button>
          <Button onClick={() => setItemGroupIds([])}>Снять все</Button>
        </div>
        <VerticalSpace size={8} />
        <div className={styles.groupTree}>
          {groupsQuery.isLoading ? (
            <LoadingView />
          ) : groupsQuery.isError ? (
            <ErrorView
              message={getErrorMessage(groupsQuery.error)}
              onRetry={groupsQuery.refetch}
            />
          ) : (
            renderGroupTree(tree)
          )}
        </div>
      </PageLayout>

      <PageLayout
        className={styles.rightPanel}
        header={
          <div className={styles.reportHeader}>
            <BoldText>{title}</BoldText>
            <div className={styles.reportSearch}>
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Поиск"
              />
            </div>
          </div>
        }
        bodyFill
      >
        {headers.length ? (
          <Table
            fill
            rowData={filteredReports}
            colDefs={colDefs}
            rowKey="number"
            bottomRow={bottomRow}
          />
        ) : (
          <MediumText color="muted" className={styles.emptyHint}>
            Выберите фильтры и нажмите «Сформировать»
          </MediumText>
        )}
      </PageLayout>
    </div>
  );
};

export const MovementReportPage = () => (
  <ReportPage title="Движение товаров" type="movement" />
);

export const ProfitReportPage = () => (
  <ReportPage
    title="Отчёт по прибыли (по сети магазинов)"
    type="profit"
    isMultiStore
  />
);
